import React, { useState } from "react";
import {
  FaTwitter,
  FaInstagram,
  FaFacebookF,
  FaYoutube,
  FaSnapchatGhost,
  FaTiktok,
} from "react-icons/fa";
import "../styles/Footer.css";
import logo from "../assets/images/logos/logo-rings.svg";
import { useTranslation } from "react-i18next";
import { mabrookEmail } from "../utils/constants";
import { useScroll } from "../context/ScrollContext";
import { useNavigate } from "react-router-dom";
import LocaleLink from "./LocaleLink";
import { useLanguage, splitLanguageFromPath } from "../context/Language";
import { isEmbedded } from "../utils/embed";
import ContactModal from "./ContactModal";

const FooterInner = () => {
  const { t } = useTranslation();
  const { sectionRefs } = useScroll();
  const navigate = useNavigate();
  const { localePath } = useLanguage();
  const [showContact, setShowContact] = useState(false);

  const scrollToSection = (section) => {
    const targetSection = sectionRefs[section]?.current;
    const { rest } = splitLanguageFromPath(window.location.pathname);
    if (!targetSection || rest !== "/") {
      navigate(`${localePath("/")}#${section}`);
    } else {
      targetSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <div className="footer-logo-lockup">
            <img src={logo} alt="Mabrook" className="footer-logo" />
            <span className="footer-wordmark">Mabrook</span>
          </div>
          <p className="footer-tagline">{t(`Premium Moroccan Matrimony`)}</p>
        </div>

        <div className="footer-col">
          <h4 className="footer-col-title">{t(`Company`)}</h4>
          <ul className="footer-col-list">
            <li
              className="footer-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("about");
              }}
            >
              {t(`About Us`)}
            </li>
            <li className="footer-link">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  setShowContact(true);
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {t(`Contact Us`)}
              </a>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/subscribe">
                {t(`Subscribe`)}
              </LocaleLink>
            </li>
          </ul>
        </div>

        <div className="footer-col">
          <h4 className="footer-col-title">{t(`Legal`)}</h4>
          <ul className="footer-col-list footer-col-list--two">
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/terms-of-service">
                {t(`Terms of Service`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/privacy-policy">
                {t(`Privacy Policy`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/moderation-policy">
                {t(`Moderation Policy`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/refund-policy">
                {t(`Refund Policy`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/cookie-policy">
                {t(`Cookie Policy`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/legal-notice">
                {t(`Legal Notice`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/child-safety">
                {t(`Child Safety`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/safety-tips">
                {t(`Safety Tips`)}
              </LocaleLink>
            </li>
            <li className="footer-link">
              <LocaleLink className="footer-inner-link" to="/withdrawal">
                {t(`Right of withdrawal`)}
              </LocaleLink>
            </li>
          </ul>
        </div>

        <div className="footer-col">
          <h4 className="footer-col-title">{t(`Get in touch`)}</h4>
          <a href={`mailto:${mabrookEmail}`} className="footer-email">
            contact@mabrook.ma
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-social">
          {[
            {
              href: "https://www.instagram.com/mabrook.app/",
              label: "Instagram",
              Icon: FaInstagram,
            },
            {
              href: "https://www.facebook.com/mabrookapp",
              label: "Facebook",
              Icon: FaFacebookF,
            },
            {
              href: "https://www.youtube.com/@mabrookapp",
              label: "YouTube",
              Icon: FaYoutube,
            },
            {
              href: "https://www.snapchat.com/add/mabrook.app",
              label: "Snapchat",
              Icon: FaSnapchatGhost,
            },
            {
              href: "https://www.tiktok.com/@mabrook.app",
              label: "TikTok",
              Icon: FaTiktok,
            },
            {
              href: "https://twitter.com/Mabrook_app",
              label: "X (Twitter)",
              Icon: FaTwitter,
            },
          ].map(({ href, label, Icon }) => (
            <a
              key={label}
              href={href}
              className="social-icon"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
            >
              <Icon />
            </a>
          ))}
        </div>
        <p className="footer-signature">
          © {new Date().getFullYear()} Mabrook. {t(`All rights reserved.`)}
        </p>
      </div>
      <ContactModal open={showContact} onClose={() => setShowContact(false)} />
    </footer>
  );
};

const Footer = () => (isEmbedded() ? null : <FooterInner />);

export default Footer;
